import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaSearch,
  FaFilter,
  FaSyncAlt,
  FaFileDownload,
  FaTruck,
  FaTimes,
  FaCheckCircle,
  FaBan,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import "./BreakdownTracker1.css";
import "./FleetMaster.css";

const emptyForm = { truck_reg: "", company_name: "" };

const FleetMaster = () => {
  const navigate = useNavigate();
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [status, setStatus] = useState("ALL");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingKey, setEditingKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const gridRef = useRef(null);

  const api = `${process.env.REACT_APP_BASE_API_URL}/api/trip-fleet`;
  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadFleet = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${api}?include_inactive=true`, {
        headers: getHeaders(),
      });
      setFleet(response.data.data || []);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Unable to load fleet master records.",
      });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadFleet();
  }, [loadFleet]);

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return fleet.filter((row) => {
      const matchesSearch =
        !query ||
        row.truck_reg?.toLowerCase().includes(query) ||
        row.truck_key?.toLowerCase().includes(query) ||
        row.company_name?.toLowerCase().includes(query);
      return matchesSearch && (status === "ALL" || row.is_active === status);
    });
  }, [fleet, searchQuery, status]);

  const companies = useMemo(
    () =>
      [...new Set(fleet.map((row) => row.company_name).filter(Boolean))].sort(),
    [fleet]
  );

  const resetFilters = () => {
    setSearchInput("");
    setSearchQuery("");
    setStatus("ALL");
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setFilterModel(null);
    }
  };

  const openAddModal = () => {
    setEditingKey(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditingKey(row.truck_key);
    setForm({ truck_reg: row.truck_reg, company_name: row.company_name });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingKey(null);
    setForm(emptyForm);
  };

  const saveTruck = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      if (editingKey) {
        await axios.put(
          `${api}/${encodeURIComponent(editingKey)}`,
          form,
          { headers: getHeaders() }
        );
      } else {
        await axios.post(api, form, { headers: getHeaders() });
      }
      setMessage({
        type: "success",
        text: editingKey
          ? `Truck ${form.truck_reg} updated successfully.`
          : `Truck ${form.truck_reg} added successfully.`,
      });
      closeModal();
      await loadFleet();
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Unable to save truck record.",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    const next = row.is_active === "Y" ? "N" : "Y";
    try {
      await axios.patch(
        `${api}/${encodeURIComponent(row.truck_key)}/status`,
        { is_active: next },
        { headers: getHeaders() }
      );
      setMessage({
        type: "success",
        text:
          next === "Y"
            ? `Truck ${row.truck_reg} activated.`
            : `Truck ${row.truck_reg} deactivated.`,
      });
      await loadFleet();
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Unable to update truck status.",
      });
    }
  };

  const handleExport = () => {
    if (filtered.length === 0) return;
    const exportData = filtered.map((r) => ({
      "Truck Registration": r.truck_reg || "",
      "System Key": r.truck_key || "",
      "Carrier / Company": r.company_name || "",
      Status: r.is_active === "Y" ? "ACTIVE" : "INACTIVE",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FleetMaster");
    const date = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `Fleet_Master_${date}.xlsx`);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Truck Reg / Plate",
        field: "truck_reg",
        sortable: true,
        filter: true,
        minWidth: 190,
        flex: 1.2,
        cellRenderer: (params) => (
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700,
              color: "#0f172a",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaTruck style={{ color: "#2563eb", fontSize: "14px" }} />
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "System Key",
        field: "truck_key",
        sortable: true,
        filter: true,
        minWidth: 160,
        flex: 1,
        cellRenderer: (params) => (
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500,
              color: "#475569",
              background: "#f1f5f9",
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "12px",
            }}
          >
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Carrier / Company",
        field: "company_name",
        sortable: true,
        filter: true,
        minWidth: 260,
        flex: 1.8,
        cellRenderer: (params) => (
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500,
              color: "#1e293b",
            }}
          >
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Status",
        field: "is_active",
        sortable: true,
        filter: true,
        minWidth: 140,
        flex: 0.9,
        cellRenderer: (params) => {
          const isActive = params.value === "Y";
          return (
            <span
              className={`fleet-status-pill ${isActive ? "active" : "inactive"}`}
            >
              <span className="fleet-status-dot" />
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        width: 170,
        minWidth: 170,
        sortable: false,
        filter: false,
        pinned: "right",
        cellRenderer: (params) => {
          const row = params.data;
          if (!row) return null;
          const isActive = row.is_active === "Y";
          return (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                height: "100%",
              }}
            >
              <button
                className="fleet-grid-btn edit"
                title="Edit Truck"
                onClick={() => openEditModal(row)}
              >
                <FaEdit /> Edit
              </button>
              <button
                className={`fleet-grid-btn ${isActive ? "deactivate" : "activate"}`}
                title={isActive ? "Deactivate Truck" : "Activate Truck"}
                onClick={() => toggleStatus(row)}
              >
                {isActive ? (
                  <>
                    <FaBan /> Deactivate
                  </>
                ) : (
                  <>
                    <FaCheckCircle /> Activate
                  </>
                )}
              </button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  );

  const hasData = filtered.length > 0;

  return (
    <div className="fleet-master-root bt-container">
      {/* Top Header */}
      <div className="bt-header">
        <div className="bt-header-main">
          <div className="bt-header-left">
            <button
              className="bt-back-btn"
              onClick={() => navigate("/main")}
              title="Back"
            >
              <FaArrowLeft />
            </button>
            <div className="bt-header-title">
              <h1>Fleet Master</h1>
            </div>
          </div>
          <div className="bt-header-actions">
            <div className="bt-filter-group">
              <FaFilter className="bt-filter-icon" />
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="bt-status-filter"
              >
                <option value="ALL">All Status</option>
                <option value="Y">Active Only</option>
                <option value="N">Inactive Only</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bt-main-content">
        {/* Feedback Alert Message */}
        {message && (
          <div className={`fleet-alert-banner ${message.type}`}>
            <span>{message.text}</span>
            <button
              type="button"
              className="fleet-alert-dismiss"
              onClick={() => setMessage(null)}
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Action Bar */}
        <div className="bt-action-bar">
          <div className="bt-action-left">
            <button onClick={openAddModal} className="bt-primary-btn">
              <FaPlus /> Add New Truck
            </button>
          </div>
          <div className="bt-action-right">
            <div className="bt-search-box">
              <FaSearch className="bt-search-icon" />
              <input
                type="text"
                placeholder="Search truck, key or carrier..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="bt-search-input"
              />
            </div>
            <button
              className="bt-icon-btn"
              onClick={resetFilters}
              title="Reset table filters"
              aria-label="Reset table filters"
              type="button"
            >
              <FaSyncAlt />
            </button>
            <button
              className="bt-export-btn"
              onClick={handleExport}
              disabled={!hasData}
            >
              <FaFileDownload /> Export Excel
            </button>
          </div>
        </div>

        {/* AG Grid Table View matching Violation/Breakdown Tracker */}
        <div className="bt-table-section">
          <div className="bt-table-container">
            {loading ? (
              <div className="fleet-loading-state">
                <div className="fleet-spinner" />
                <p>Loading fleet master records...</p>
              </div>
            ) : hasData ? (
              <div className="ag-theme-alpine bt-ag-grid">
                <AgGridReact
                  ref={gridRef}
                  rowData={filtered}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowHeight={42}
                  headerHeight={40}
                  pagination={true}
                  paginationPageSize={25}
                  animateRows={true}
                />
              </div>
            ) : (
              <div className="bt-empty-state">
                <FaTruck className="bt-empty-icon" />
                <h3>No fleet records found</h3>
                <p>
                  Try adjusting your search query or status filter, or register
                  a new truck
                </p>
                <button onClick={openAddModal} className="bt-primary-btn">
                  <FaPlus /> Add First Truck
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit Truck Modal */}
      {modalOpen && (
        <div className="fleet-modal-backdrop" onClick={closeModal}>
          <div
            className="fleet-modal-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fleet-modal-header">
              <div className="fleet-modal-titles">
                <h2>{editingKey ? "Edit Fleet Truck" : "Add New Fleet Truck"}</h2>
                <p>
                  {editingKey
                    ? `Update vehicle details for system key: ${editingKey}`
                    : "Register an approved vehicle into the JMCC fleet database"}
                </p>
              </div>
              <button
                type="button"
                className="fleet-modal-close-btn"
                onClick={closeModal}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={saveTruck} className="fleet-modal-form">
              <div className="fleet-modal-field">
                <label className="fleet-modal-label">
                  Truck Registration <span className="req">*</span>
                </label>
                <input
                  type="text"
                  className="fleet-modal-input"
                  placeholder="e.g. 1258/MR"
                  value={form.truck_reg}
                  onChange={(e) =>
                    setForm({ ...form, truck_reg: e.target.value })
                  }
                  required
                  autoFocus
                />
                <span className="fleet-field-help">
                  Standard format: digits followed by / and letter code
                </span>
              </div>

              <div className="fleet-modal-field">
                <label className="fleet-modal-label">
                  Carrier / Company Name <span className="req">*</span>
                </label>
                <input
                  type="text"
                  className="fleet-modal-input"
                  placeholder="e.g. Al Madina Logistics"
                  value={form.company_name}
                  onChange={(e) =>
                    setForm({ ...form, company_name: e.target.value })
                  }
                  list="fleet-company-datalist"
                  required
                />
                <datalist id="fleet-company-datalist">
                  {companies.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                <span className="fleet-field-help">
                  Select from existing carriers or enter a new carrier name
                </span>
              </div>

              <div className="fleet-modal-actions">
                <button
                  type="button"
                  className="fleet-modal-cancel"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="fleet-modal-submit"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingKey
                    ? "Save Changes"
                    : "Add Truck"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetMaster;
